import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, expenseCategories, paymentModes, getCategoryLabel } from '../lib/utils';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { DesktopOnlyFeature, useIsMobile } from '../lib/responsive';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  CreditCard as CreditCardIcon,
  Plus,
  IndianRupee,
  Receipt,
  Wallet,
  Trash2,
  ArrowDownRight,
  ArrowUpRight,
  Search,
  Upload,
  FileText,
  Check,
  X,
  CheckCheck,
  RotateCcw,
  Loader2,
  Monitor,
  Shield,
  ShieldCheck,
  ShieldX,
  Briefcase,
  User,
  SplitSquareHorizontal,
  MoreHorizontal,
  Filter
} from 'lucide-react';

export default function CreditCards() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [cards, setCards] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addTransactionOpen, setAddTransactionOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [ccStatementOpen, setCcStatementOpen] = useState(false);
  const [ccFiles, setCcFiles] = useState([]);
  const [ccParsedTxs, setCcParsedTxs] = useState([]);
  const [ccBatchId, setCcBatchId] = useState('');
  const [ccAccepted, setCcAccepted] = useState({});
  const [ccParseLoading, setCcParseLoading] = useState(false);
  const [ccStep, setCcStep] = useState(1);
  const [ccPdfPassword, setCcPdfPassword] = useState('');
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [bulkVerifyOpen, setBulkVerifyOpen] = useState(false);
  const [bulkVerifyVenture, setBulkVerifyVenture] = useState('');
  const [bulkVerifyLocation, setBulkVerifyLocation] = useState('');
  const [selectedTxIds, setSelectedTxIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // all, verified, unverified
  const [filterType, setFilterType] = useState('all'); // all, business, personal, split
  const allCategories = [...expenseCategories, ...customCategories.map(c => ({ value: c.value, label: c.label }))];
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCard) {
      fetchTransactions(selectedCard.card_id);
      fetchMonthlySummary(selectedCard.card_id);
    }
  }, [selectedCard]);

  const fetchData = async () => {
    try {
      const [cardsRes, venturesRes, locationsRes, catsRes] = await Promise.all([
        api.get('/credit-cards'),
        api.get('/ventures'),
        api.get('/locations'),
        api.get('/categories/custom').catch(() => ({ data: [] }))
      ]);
      setCards(cardsRes.data);
      setVentures(venturesRes.data);
      setLocations(locationsRes.data);
      setCustomCategories(catsRes.data);
      
      if (cardsRes.data.length > 0 && !selectedCard) {
        setSelectedCard(cardsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (cardId) => {
    try {
      const res = await api.get(`/credit-cards/${cardId}/transactions`);
      setTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchMonthlySummary = async (cardId) => {
    try {
      const res = await api.get(`/credit-cards/${cardId}/monthly-summary`);
      setMonthlySummary(res.data.monthly_summaries || []);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this credit card?')) return;
    
    try {
      await api.delete(`/credit-cards/${cardId}`);
      toast.success('Credit card deleted');
      setSelectedCard(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete credit card');
    }
  };

  const handleFixBusinessPersonal = async () => {
    try {
      const res = await api.post('/credit-cards/fix-business-personal');
      toast.success(res.data.message || 'Amounts fixed');
      if (selectedCard) fetchTransactions(selectedCard.card_id);
    } catch (error) {
      toast.error('Failed to fix amounts');
    }
  };

  const totalOutstanding = cards.reduce((sum, card) => sum + card.outstanding_balance, 0);
  const totalLimit = cards.reduce((sum, card) => sum + card.credit_limit, 0);

  const filteredTransactions = transactions.filter(tx => {
    // Status filter
    if (filterStatus !== 'all') {
      const status = tx.verification_status || 'unverified';
      if (filterStatus !== status) return false;
    }
    // Type filter
    if (filterType !== 'all') {
      const type = tx.transaction_type || 'business';
      if (filterType !== type) return false;
    }
    // Search filter
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return tx.description?.toLowerCase().includes(q) || tx.category?.toLowerCase().includes(q);
  });

  const unverifiedCount = transactions.filter(tx => (tx.verification_status || 'unverified') === 'unverified').length;
  const selectedUnverified = selectedTxIds.filter(id => {
    const tx = transactions.find(t => t.transaction_id === id);
    return tx && (tx.verification_status || 'unverified') === 'unverified';
  });

  const handleBulkVerify = async () => {
    if (selectedUnverified.length === 0) {
      toast.error('Select unverified transactions first');
      return;
    }
    try {
      await api.post('/credit-cards/transactions/bulk-verify', {
        transaction_ids: selectedUnverified,
        venture_id: bulkVerifyVenture || null,
        location_id: bulkVerifyLocation || null
      });
      toast.success(`Verified ${selectedUnverified.length} transactions`);
      setBulkVerifyOpen(false);
      setSelectedTxIds([]);
      setBulkVerifyVenture('');
      setBulkVerifyLocation('');
      fetchTransactions(selectedCard.card_id);
      fetchData();
    } catch (error) {
      toast.error('Failed to bulk verify');
    }
  };

  const toggleSelectAll = () => {
    if (selectedTxIds.length === filteredTransactions.length) {
      setSelectedTxIds([]);
    } else {
      setSelectedTxIds(filteredTransactions.map(tx => tx.transaction_id));
    }
  };

  const [ccParseProgress, setCcParseProgress] = useState('');

  const handleParseCCStatement = async () => {
    if (ccFiles.length === 0 || !selectedCard) return;
    setCcParseLoading(true);
    try {
      let allTransactions = [];
      let batchIds = [];

      for (let i = 0; i < ccFiles.length; i++) {
        setCcParseProgress(`Parsing file ${i + 1} of ${ccFiles.length}: ${ccFiles[i].name}`);
        const formData = new FormData();
        formData.append('file', ccFiles[i]);
        formData.append('card_id', selectedCard.card_id);
        if (ccPdfPassword) formData.append('pdf_password', ccPdfPassword);

        const res = await api.post('/credit-cards/parse-statement-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        // Tag each transaction with its batch_id for proper confirm
        const tagged = res.data.transactions.map(tx => ({ ...tx, _batch_id: res.data.batch_id }));
        allTransactions = [...allTransactions, ...tagged];
        batchIds.push(res.data.batch_id);
      }

      setCcParsedTxs(allTransactions);
      setCcBatchId(batchIds); // Store ALL batch IDs
      const initial = {};
      allTransactions.forEach((tx, i) => { initial[i] = tx.confidence === 'high' || tx.auto_matched; });
      setCcAccepted(initial);
      setCcStep(2);
      setCcParseProgress('');
      toast.success(`Parsed ${allTransactions.length} transactions from ${ccFiles.length} file(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to parse statement');
      setCcParseProgress('');
    } finally {
      setCcParseLoading(false);
    }
  };

  const handleCCCategoryChange = async (index, newCategory) => {
    const tx = ccParsedTxs[index];
    try {
      const res = await api.patch('/credit-cards/update-statement-category', null, {
        params: { batch_id: tx._batch_id || (Array.isArray(ccBatchId) ? ccBatchId[0] : ccBatchId), description: tx.description, new_category: newCategory }
      });
      // Update locally instead of replacing entire array (preserves other changes)
      const updated = [...ccParsedTxs];
      const vendorKey = tx.vendor_key;
      const newAcc = { ...ccAccepted, [index]: true };
      
      updated[index] = { ...tx, category: newCategory, confidence: 'high' };
      
      if (vendorKey) {
        updated.forEach((t, i) => {
          if (t.vendor_key === vendorKey) {
            updated[i] = { ...t, category: newCategory, confidence: 'high' };
            newAcc[i] = true;
          }
        });
      }
      
      setCcParsedTxs(updated);
      setCcAccepted(newAcc);
      if ((res.data.similar_updated || 0) > 1) toast.success(`Updated ${res.data.similar_updated} similar transactions`);
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleCCConfirm = async () => {
    const acceptedIndices = Object.entries(ccAccepted).filter(([, v]) => v).map(([k]) => parseInt(k));
    if (acceptedIndices.length === 0) { toast.error('Accept at least one transaction'); return; }
    setCcParseLoading(true);
    try {
      // Group accepted transactions by batch_id
      const batchGroups = {};
      acceptedIndices.forEach(idx => {
        const tx = ccParsedTxs[idx];
        const bid = tx._batch_id || (Array.isArray(ccBatchId) ? ccBatchId[0] : ccBatchId);
        if (!batchGroups[bid]) batchGroups[bid] = { indices: [], overrides: {} };
        batchGroups[bid].indices.push(idx);
      });

      let totalImported = 0;
      let totalSkipped = 0;

      // Confirm each batch separately
      for (const [bid, group] of Object.entries(batchGroups)) {
        // Recalculate indices relative to this batch's transactions
        const batchTxIndices = ccParsedTxs.reduce((acc, tx, i) => {
          if ((tx._batch_id || '') === bid) acc.push(i);
          return acc;
        }, []);
        const relativeIndices = group.indices.map(globalIdx => batchTxIndices.indexOf(globalIdx)).filter(i => i >= 0);

        // Build overrides map: relative_index -> {category, is_business, transaction_type}
        const overrides = {};
        group.indices.forEach(globalIdx => {
          const relIdx = batchTxIndices.indexOf(globalIdx);
          if (relIdx >= 0) {
            const tx = ccParsedTxs[globalIdx];
            overrides[relIdx] = {
              category: tx.category,
              is_business: tx.is_business !== false,
              transaction_type: tx.is_business === false ? 'personal' : 'business'
            };
          }
        });

        const res = await api.post('/credit-cards/confirm-statement', {
          batch_id: bid,
          card_id: selectedCard.card_id,
          accepted_indices: relativeIndices,
          transaction_overrides: overrides
        });
        totalImported += res.data.imported || 0;
        totalSkipped += res.data.skipped_duplicates || 0;
      }

      let msg = `Imported ${totalImported} transactions`;
      if (totalSkipped > 0) msg += ` (${totalSkipped} duplicates skipped)`;
      toast.success(msg);
      setCcStep(1);
      setCcStatementOpen(false);
      setCcFiles([]);
      setCcParsedTxs([]);
      fetchTransactions(selectedCard.card_id);
      fetchData();
    } catch (error) {
      console.error('Import error:', error);
      const errMsg = error?.response?.data?.detail || error?.message || 'Failed to import';
      toast.error(errMsg);
    } finally {
      setCcParseLoading(false);
    }
  };

  const ccAcceptedCount = Object.values(ccAccepted).filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="credit-cards-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Credit Cards</h1>
          <p className="text-zinc-500 mt-1">Track credit card expenses with business/personal split</p>
        </div>
        <Button 
          onClick={() => setAddCardOpen(true)}
          className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600"
          data-testid="add-card-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Card
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="roch-card metric-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <CreditCardIcon className="w-4 h-4 text-red-600" />
            </div>
            <div className="metric-label mb-0">Total Outstanding</div>
          </div>
          <div className="metric-value currency text-red-600">{formatCurrency(totalOutstanding)}</div>
        </Card>
        <Card className="roch-card metric-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-zinc-600" />
            </div>
            <div className="metric-label mb-0">Total Credit Limit</div>
          </div>
          <div className="metric-value currency">{formatCurrency(totalLimit)}</div>
        </Card>
        <Card className="roch-card metric-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Receipt className="w-4 h-4 text-green-600" />
            </div>
            <div className="metric-label mb-0">Available Credit</div>
          </div>
          <div className="metric-value currency text-green-600">{formatCurrency(totalLimit - totalOutstanding)}</div>
        </Card>
      </div>

      {cards.length === 0 ? (
        <Card className="roch-card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <CreditCardIcon className="w-6 h-6" />
            </div>
            <h3 className="font-medium text-zinc-900 mb-1">No credit cards added</h3>
            <p className="text-sm text-zinc-500 mb-4">Add your credit cards to track expenses</p>
            <Button onClick={() => setAddCardOpen(true)}>Add Credit Card</Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cards List */}
          <div className="lg:col-span-1 space-y-4">
            {cards.map((card) => {
              const utilization = (card.outstanding_balance / card.credit_limit) * 100;
              return (
                <Card 
                  key={card.card_id}
                  className={`roch-card cursor-pointer transition-all ${
                    selectedCard?.card_id === card.card_id 
                      ? 'ring-2 ring-amber-500' 
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedCard(card)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg flex items-center justify-center">
                          <CreditCardIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-zinc-900">{card.name}</h3>
                          <p className="text-xs text-zinc-500">Limit: {formatCurrency(card.credit_limit)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.card_id); }}
                        className="text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Outstanding</span>
                        <span className="font-semibold text-red-600">{formatCurrency(card.outstanding_balance)}</span>
                      </div>
                      <Progress 
                        value={Math.min(100, utilization)} 
                        className={`h-2 ${utilization > 80 ? '[&>div]:bg-red-500' : utilization > 50 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'}`}
                      />
                      <p className="text-xs text-zinc-400 text-right">{utilization.toFixed(0)}% utilized</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Card Details */}
          {selectedCard && (
            <div className="lg:col-span-2 space-y-6">
              {/* Actions */}
              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => setAddTransactionOpen(true)} className="flex-1 min-w-[140px] rounded-xl">
                  <ArrowDownRight className="w-4 h-4 mr-2" />
                  Add Transaction
                </Button>
                {isMobile ? (
                  <Button variant="outline" className="flex-1 min-w-[140px] rounded-xl opacity-50 cursor-not-allowed" disabled>
                    <Monitor className="w-4 h-4 mr-2" />
                    Import (Desktop)
                  </Button>
                ) : (
                  <Button onClick={() => setCcStatementOpen(true)} variant="outline" className="flex-1 min-w-[140px] rounded-xl" data-testid="import-cc-statement-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Import Statement
                  </Button>
                )}
                <Button onClick={() => setAddPaymentOpen(true)} variant="outline" className="flex-1 min-w-[140px] rounded-xl">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              </div>

              {/* View Toggle & Transactions/Monthly Summary */}
              <Card className="roch-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setShowMonthlySummary(false)}
                      className={`text-base font-medium pb-1 border-b-2 transition-colors ${!showMonthlySummary ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
                    >
                      Recent Transactions
                      {unverifiedCount > 0 && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">{unverifiedCount} unverified</span>
                      )}
                    </button>
                    <button
                      onClick={() => setShowMonthlySummary(true)}
                      className={`text-base font-medium pb-1 border-b-2 transition-colors ${showMonthlySummary ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
                    >
                      Monthly Summary
                    </button>
                  </div>
                  {!showMonthlySummary && (
                    <div className="flex items-center gap-2">
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="cc-filter-status">
                          <Shield className="w-3 h-3 mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="unverified">Unverified</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="cc-filter-type">
                          <Briefcase className="w-3 h-3 mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="personal">Personal</SelectItem>
                          <SelectItem value="split">Split</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative w-44">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9"
                          data-testid="cc-transactions-search"
                        />
                      </div>
                      {selectedTxIds.length > 0 && (
                        <Button size="sm" onClick={() => setBulkVerifyOpen(true)} className="bg-green-600 hover:bg-green-700" data-testid="bulk-verify-btn">
                          <ShieldCheck className="w-4 h-4 mr-1" />
                          Verify ({selectedUnverified.length})
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={handleFixBusinessPersonal} data-testid="fix-amounts-btn" className="text-xs">
                        Fix Business/Personal
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {showMonthlySummary ? (
                    // Monthly Summary View
                    monthlySummary.length === 0 ? (
                      <div className="text-center py-8 text-sm text-zinc-500">
                        No monthly data available
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-100">
                        {monthlySummary.map((month) => (
                          <div key={month.month} className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-zinc-900">{formatMonth(month.month)}</h4>
                                <p className="text-xs text-zinc-500">
                                  {month.debit_count || month.transaction_count} charges
                                  {month.credit_count > 0 && `, ${month.credit_count} payments/refunds`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-red-600">{formatCurrency(month.total_spent)}</p>
                                <p className="text-xs text-zinc-500">Total Spent</p>
                              </div>
                            </div>
                            
                            {/* Credits/Payments section */}
                            {month.total_credits > 0 && (
                              <div className="flex items-center justify-between mb-3 py-2 px-3 bg-emerald-50 rounded-lg">
                                <span className="text-sm text-emerald-700">Payments/Refunds</span>
                                <span className="font-semibold text-emerald-600">+{formatCurrency(month.total_credits)}</span>
                              </div>
                            )}
                            
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(month.by_category || {})
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 5)
                                .map(([cat, amt]) => (
                                  <span key={cat} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 text-xs">
                                    <span className="text-zinc-600">{getCategoryLabel(cat)}</span>
                                    <span className="font-medium">{formatCurrency(amt)}</span>
                                  </span>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    // Transactions View
                    transactions.length === 0 ? (
                      <div className="text-center py-8 text-sm text-zinc-500">
                        No transactions recorded
                      </div>
                    ) : (
                      <div className="roch-table-wrap">
                      <table className="roch-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th className="text-center">Type</th>
                            <th className="text-center">Status</th>
                            <th className="text-right">Business</th>
                            <th className="text-right">Personal</th>
                            <th className="text-right">Total</th>
                            <th className="w-20"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTransactions.map((tx) => (
                            <TransactionRow
                              key={tx.transaction_id}
                              tx={tx}
                              onUpdate={() => { fetchTransactions(selectedCard.card_id); fetchData(); }}
                            />
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Add Card Dialog */}
      <AddCardDialog
        open={addCardOpen}
        onOpenChange={setAddCardOpen}
        onSuccess={fetchData}
      />

      {/* Add Transaction Dialog */}
      {selectedCard && (
        <AddTransactionDialog
          open={addTransactionOpen}
          onOpenChange={setAddTransactionOpen}
          card={selectedCard}
          ventures={ventures}
          locations={locations}
          allCategories={allCategories}
          onSuccess={() => { fetchData(); fetchTransactions(selectedCard.card_id); }}
        />
      )}

      {/* Add Payment Dialog */}
      {selectedCard && (
        <AddPaymentDialog
          open={addPaymentOpen}
          onOpenChange={setAddPaymentOpen}
          card={selectedCard}
          onSuccess={fetchData}
        />
      )}

      {/* CC Statement Upload Dialog */}
      {selectedCard && (
        <Dialog open={ccStatementOpen} onOpenChange={(o) => { if (!o) { setCcStep(1); setCcParsedTxs([]); setCcFiles([]); } setCcStatementOpen(o); }}>
          <DialogContent className="sm:max-w-[850px] max-h-[85vh] overflow-y-auto" data-testid="cc-statement-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-amber-500" />
                Import Credit Card Statement - {selectedCard.name}
              </DialogTitle>
            </DialogHeader>

            {ccStep === 1 && (
              <div className="space-y-4 py-4">
                <p className="text-sm text-zinc-500">Upload one or more credit card statements. AI will categorize each transaction and split business/personal.</p>
                <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors" onClick={() => document.getElementById('cc-file-input')?.click()}>
                  <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-zinc-700">{ccFiles.length > 0 ? `${ccFiles.length} file(s) selected` : 'Click to upload statement(s)'}</p>
                  <p className="text-xs text-zinc-400 mt-1">PDF, PNG, JPG, Excel up to 10MB each</p>
                  <input id="cc-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls" multiple className="hidden" onChange={(e) => setCcFiles(Array.from(e.target.files))} data-testid="cc-file-input" />
                </div>
                {ccFiles.length > 0 && (
                  <div className="space-y-1">
                    {ccFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-zinc-50 rounded-md px-3 py-1.5 text-xs">
                        <span className="text-zinc-700 truncate">{f.name}</span>
                        <button onClick={() => setCcFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-500 ml-2"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {ccFiles.some(f => f.name.toLowerCase().endsWith('.pdf')) && (
                  <div>
                    <Label>PDF Password <span className="text-zinc-400 font-normal">(if protected)</span></Label>
                    <Input type="password" placeholder="Leave blank if not protected" value={ccPdfPassword} onChange={(e) => setCcPdfPassword(e.target.value)} className="mt-1.5" data-testid="cc-pdf-password" />
                  </div>
                )}
                <Button onClick={handleParseCCStatement} disabled={ccParseLoading || ccFiles.length === 0} className="w-full" data-testid="cc-parse-btn">
                  {ccParseLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{ccParseProgress || 'Parsing with AI...'}</> : <><FileText className="w-4 h-4 mr-2" />Parse {ccFiles.length > 1 ? `${ccFiles.length} Statements` : 'Statement'}</>}
                </Button>
              </div>
            )}

            {ccStep === 2 && (
              <div className="space-y-3 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 flex-1 mr-3">
                    Review and confirm each transaction. Change category if needed.
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => { const all = {}; ccParsedTxs.forEach((_, i) => { all[i] = true; }); setCcAccepted(all); }}>
                      <CheckCheck className="w-3.5 h-3.5 mr-1" />All
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { const all = {}; ccParsedTxs.forEach((_, i) => { all[i] = false; }); setCcAccepted(all); }}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Reset
                    </Button>
                  </div>
                </div>

                <div className="max-h-[380px] overflow-auto border rounded-lg" data-testid="cc-parsed-txs">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-zinc-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-2.5 font-medium text-xs w-24">Date</th>
                        <th className="text-left p-2.5 font-medium text-xs">Description</th>
                        <th className="text-left p-2.5 font-medium text-xs w-36">Category</th>
                        <th className="text-center p-2.5 font-medium text-xs w-16">Type</th>
                        <th className="text-center p-2.5 font-medium text-xs w-16">Scope</th>
                        <th className="text-right p-2.5 font-medium text-xs w-24">Amount</th>
                        <th className="text-center p-2.5 font-medium text-xs w-20">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ccParsedTxs.map((tx, i) => (
                        <tr key={i} className={`border-b transition-colors ${ccAccepted[i] ? 'bg-green-50/50' : ccAccepted[i] === false ? 'bg-red-50/30 opacity-60' : tx.confidence === 'low' ? 'bg-amber-50/50' : ''}`}>
                          <td className="p-2.5 text-xs text-zinc-500">{formatDate(tx.date)}</td>
                          <td className="p-2.5">
                            <div className="truncate max-w-[180px] text-xs" title={tx.description}>{tx.description}</div>
                            <div className="flex gap-1 mt-0.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${tx.confidence === 'high' ? 'bg-green-50 text-green-700 border-green-200' : tx.confidence === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{tx.confidence}</span>
                              {tx.auto_matched && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">auto</span>}
                              {tx.group_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">{tx.group_count}x</span>}
                            </div>
                          </td>
                          <td className="p-2.5">
                            <Select value={tx.category} onValueChange={(v) => handleCCCategoryChange(i, v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {allCategories.map(c => <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>)}
                                <SelectItem value="investment" className="text-xs">Investment</SelectItem>
                                <SelectItem value="loan_emi" className="text-xs">Loan EMI</SelectItem>
                                <SelectItem value="other" className="text-xs">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2.5 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${tx.type === 'credit' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                              {tx.type === 'credit' ? 'Cr' : 'Dr'}
                            </span>
                          </td>
                          <td className="p-2.5 text-center">
                            <button onClick={() => { const updated = [...ccParsedTxs]; updated[i] = { ...tx, is_business: !tx.is_business }; setCcParsedTxs(updated); }} className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${tx.is_business ? 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100' : 'bg-purple-50 text-purple-600 border border-purple-200 hover:bg-purple-100'}`}>
                              {tx.is_business ? 'Biz' : 'Personal'}
                            </button>
                          </td>
                          <td className={`p-2.5 text-right font-medium text-xs ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>{tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}</td>
                          <td className="p-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => setCcAccepted(p => ({ ...p, [i]: true }))} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${ccAccepted[i] ? 'bg-green-500 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-green-100 hover:text-green-600'}`}>
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setCcAccepted(p => ({ ...p, [i]: false }))} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${ccAccepted[i] === false ? 'bg-red-500 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-red-100 hover:text-red-600'}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Button variant="outline" onClick={() => setCcStep(1)}>Back</Button>
                  <span className="text-sm text-zinc-500"><span className="font-medium text-green-600">{ccAcceptedCount}</span> of {ccParsedTxs.length} accepted</span>
                  <Button onClick={handleCCConfirm} disabled={ccParseLoading || ccAcceptedCount === 0} data-testid="cc-confirm-btn">
                    {ccParseLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Check className="w-4 h-4 mr-2" />Import {ccAcceptedCount}</>}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Verify Dialog */}
      {selectedCard && (
        <BulkVerifyDialog
          open={bulkVerifyOpen}
          onOpenChange={setBulkVerifyOpen}
          count={selectedUnverified.length}
          ventures={ventures}
          locations={locations}
          onConfirm={handleBulkVerify}
          ventureId={bulkVerifyVenture}
          setVentureId={setBulkVerifyVenture}
          locationId={bulkVerifyLocation}
          setLocationId={setBulkVerifyLocation}
        />
      )}
    </div>
  );
}

function AddCardDialog({ open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', credit_limit: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.credit_limit) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/credit-cards', { name: form.name, credit_limit: parseFloat(form.credit_limit) });
      toast.success('Credit card added');
      setForm({ name: '', credit_limit: '' });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error('Failed to add credit card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add Credit Card</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Card Name</Label>
            <Input
              placeholder="e.g., HDFC Business Card"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Credit Limit (₹)</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                type="number"
                placeholder="0"
                value={form.credit_limit}
                onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                className="pl-9"
              />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Adding...' : 'Add Card'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddTransactionDialog({ open, onOpenChange, card, ventures, locations, allCategories, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    total_amount: '',
    business_amount: '',
    personal_amount: '',
    venture_id: '',
    location_id: '',
    category: ''
  });

  const filteredLocations = locations.filter(l => l.venture_id === form.venture_id);

  // Auto-calculate split
  const handleTotalChange = (total) => {
    setForm({ ...form, total_amount: total, business_amount: total, personal_amount: '0' });
  };

  const handleBusinessChange = (business) => {
    const total = parseFloat(form.total_amount) || 0;
    const personal = Math.max(0, total - parseFloat(business || 0));
    setForm({ ...form, business_amount: business, personal_amount: personal.toString() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.total_amount) {
      toast.error('Please fill required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/credit-cards/transactions', {
        card_id: card.card_id,
        date: form.date,
        description: form.description,
        total_amount: parseFloat(form.total_amount),
        business_amount: parseFloat(form.business_amount || 0),
        personal_amount: parseFloat(form.personal_amount || 0),
        venture_id: form.venture_id || null,
        location_id: form.location_id || null,
        category: form.category || null
      });
      toast.success('Transaction recorded');
      setForm({
        date: new Date().toISOString().split('T')[0],
        description: '',
        total_amount: '',
        business_amount: '',
        personal_amount: '',
        venture_id: '',
        location_id: '',
        category: ''
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error('Failed to record transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Card Transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Total Amount (₹)</Label>
              <div className="relative mt-1.5">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="number"
                  placeholder="0"
                  value={form.total_amount}
                  onChange={(e) => handleTotalChange(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              placeholder="Transaction description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1.5"
            />
          </div>

          <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
            <p className="text-sm font-medium text-zinc-700">Split Transaction</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Business Portion (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="number"
                    placeholder="0"
                    value={form.business_amount}
                    onChange={(e) => handleBusinessChange(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Personal Portion (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" />
                  <Input
                    type="number"
                    placeholder="0"
                    value={form.personal_amount}
                    onChange={(e) => setForm({ ...form, personal_amount: e.target.value })}
                    className="pl-9 bg-amber-50"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Venture (for business)</Label>
              <Select
                value={form.venture_id}
                onValueChange={(value) => setForm({ ...form, venture_id: value, location_id: '' })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select venture" />
                </SelectTrigger>
                <SelectContent>
                  {ventures.map((v) => (
                    <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(value) => setForm({ ...form, category: value })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.venture_id && (
            <div>
              <Label>Location</Label>
              <Select
                value={form.location_id}
                onValueChange={(value) => setForm({ ...form, location_id: value })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {filteredLocations.map((loc) => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Recording...' : 'Record Transaction'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentDialog({ open, onOpenChange, card, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    payment_mode: 'bank_transfer'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount) {
      toast.error('Please enter payment amount');
      return;
    }

    setLoading(true);
    try {
      await api.post('/credit-cards/payment', {
        card_id: card.card_id,
        amount: parseFloat(form.amount),
        date: form.date,
        payment_mode: form.payment_mode
      });
      toast.success('Payment recorded');
      setForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        payment_mode: 'bank_transfer'
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Record Card Payment</DialogTitle>
        </DialogHeader>
        <div className="p-3 bg-zinc-50 rounded-lg mb-4">
          <p className="text-sm text-zinc-500">Outstanding Balance</p>
          <p className="text-xl font-semibold text-red-600">{formatCurrency(card.outstanding_balance)}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Payment Amount (₹)</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                type="number"
                placeholder="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="pl-9"
              />
            </div>
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Payment Mode</Label>
            <Select
              value={form.payment_mode}
              onValueChange={(value) => setForm({ ...form, payment_mode: value })}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paymentModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Recording...' : 'Record Payment'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// TransactionRow component for inline editing
function TransactionRow({ tx, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  
  const handleToggleVerification = async () => {
    setUpdating(true);
    try {
      const newStatus = (tx.verification_status || 'unverified') === 'verified' ? 'unverified' : 'verified';
      await api.patch(`/credit-cards/transactions/${tx.transaction_id}`, {
        verification_status: newStatus
      });
      toast.success(newStatus === 'verified' ? 'Transaction verified' : 'Verification removed');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangeType = async (newType) => {
    setUpdating(true);
    try {
      await api.patch(`/credit-cards/transactions/${tx.transaction_id}`, {
        transaction_type: newType
      });
      toast.success(`Changed to ${newType}`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const isVerified = (tx.verification_status || 'unverified') === 'verified';
  const txType = tx.transaction_type || 'business';

  return (
    <tr className={`${isVerified ? 'bg-green-50/30' : ''} ${updating ? 'opacity-50' : ''}`} data-testid={`cc-tx-row-${tx.transaction_id}`}>
      <td className="text-zinc-500">{formatDate(tx.date)}</td>
      <td>
        <div className="font-medium">{tx.description}</div>
        {tx.category && (
          <span className="text-xs text-zinc-400">{getCategoryLabel(tx.category)}</span>
        )}
      </td>
      <td className="text-center">
        <Select value={txType} onValueChange={handleChangeType} disabled={updating}>
          <SelectTrigger className="h-7 w-[100px] text-xs mx-auto" data-testid={`tx-type-select-${tx.transaction_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="business">
              <span className="flex items-center gap-1.5">
                <Briefcase className="w-3 h-3 text-blue-600" />
                Business
              </span>
            </SelectItem>
            <SelectItem value="personal">
              <span className="flex items-center gap-1.5">
                <User className="w-3 h-3 text-purple-600" />
                Personal
              </span>
            </SelectItem>
            <SelectItem value="split">
              <span className="flex items-center gap-1.5">
                <SplitSquareHorizontal className="w-3 h-3 text-amber-600" />
                Split
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="text-center">
        <button
          onClick={handleToggleVerification}
          disabled={updating}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
            isVerified 
              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
          }`}
          data-testid={`tx-verify-toggle-${tx.transaction_id}`}
        >
          {isVerified ? (
            <>
              <ShieldCheck className="w-3 h-3" />
              Verified
            </>
          ) : (
            <>
              <ShieldX className="w-3 h-3" />
              Unverified
            </>
          )}
        </button>
      </td>
      <td className="text-right text-zinc-600">{formatCurrency(tx.business_amount ?? tx.amount ?? 0)}</td>
      <td className="text-right text-amber-600">{formatCurrency(tx.personal_amount ?? 0)}</td>
      <td className="text-right font-medium">{formatCurrency(tx.total_amount ?? tx.amount ?? 0)}</td>
      <td></td>
    </tr>
  );
}

// Bulk Verify Dialog
function BulkVerifyDialog({ open, onOpenChange, count, ventures, locations, onConfirm, ventureId, setVentureId, locationId, setLocationId }) {
  const filteredLocations = locations.filter(l => l.venture_id === ventureId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="bulk-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" />
            Bulk Verify Transactions
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              You are about to verify <span className="font-bold">{count}</span> unverified transaction(s).
            </p>
            <p className="text-xs text-green-600 mt-1">
              Only verified business transactions are counted in P&L.
            </p>
          </div>
          
          <div>
            <Label>Assign Venture (Optional)</Label>
            <Select value={ventureId} onValueChange={(v) => { setVentureId(v); setLocationId(''); }}>
              <SelectTrigger className="mt-1.5" data-testid="bulk-verify-venture">
                <SelectValue placeholder="Select venture" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">No venture</SelectItem>
                {ventures.map((v) => (
                  <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {ventureId && (
            <div>
              <Label>Assign Location (Optional)</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-verify-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No location</SelectItem>
                  {filteredLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={onConfirm} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="confirm-bulk-verify">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verify {count}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
